import React, { useMemo, useState } from 'react';
import './JungleSimulatorPage.css';
import {
  toSec,
  fmt,
  champLevelFromXP,
  CAMPS,
  lerpByLevel,
  wolvesXPOverride,
  getWaveGold,
  getWaveXP,
  getWaveTiming,
  type CampId,
} from './utils';
import SummonersRiftMap from './SummonersRiftMap';
import MapCoordinatePlacer from './MapCoordinatePlacer';
// WallPainter is now on its own page at /map-editor

const FIRST_CLEAR_ORDER: CampId[] = ['blue', 'gromp', 'wolves', 'raptors', 'red', 'krugs', 'scuttle'];

const JungleSimulatorPage: React.FC = () => {
  // Adjustable assumptions (defaults match your scenario)
  const [start, setStart] = useState<number>(toSec('1:30'));
  const [segment, setSegment] = useState({
    blue: 15,
    gromp: 35,
    wolves: 50,
    raptors: 25,
    red: 45,
    krugs: 55,
  });
  const [takeScuttle, setTakeScuttle] = useState<boolean>(true);
  const [scuttleTime, setScuttleTime] = useState<number>(toSec('3:30'));
  const [baseSeconds, setBaseSeconds] = useState<number>(7);
  const [walkSeconds, setWalkSeconds] = useState<number>(25);
  const [useWolvesOverride, setUseWolvesOverride] = useState<boolean>(true);
  const [monsterLevelMax, setMonsterLevelMax] = useState<number>(18);
  const [otherScuttleKilledAt, setOtherScuttleKilledAt] = useState<number>(toSec('3:40'));

  // Build first clear timeline
  const firstClear = useMemo(() => {
    const t: Partial<Record<CampId, number>> = {};
    t.blue = start + segment.blue;
    t.gromp = start + segment.gromp;
    t.wolves = start + segment.wolves;
    const second = t.wolves!;
    t.raptors = second + segment.raptors;
    t.red = second + segment.red;
    t.krugs = second + segment.krugs;
    t.scuttle = takeScuttle ? scuttleTime : undefined;
    return t as Record<CampId, number | undefined>;
  }, [start, segment, scuttleTime, takeScuttle]);

  // Respawns
  const respawns = useMemo(() => {
    const r: Partial<Record<CampId, number>> = {};
    (['blue', 'gromp', 'wolves', 'raptors', 'red', 'krugs'] as CampId[]).forEach((id) => {
      const kill = firstClear[id];
      if (kill) r[id] = kill + CAMPS[id].respawn;
    });
    if (firstClear.scuttle) {
      const pairCleared = Math.max(firstClear.scuttle, otherScuttleKilledAt);
      r.scuttle = pairCleared + CAMPS.scuttle.respawn;
    }
    return r as Record<CampId, number | undefined>;
  }, [firstClear, otherScuttleKilledAt]);

  // Which camp is next after we take Scuttle?
  const nextAfterScuttle = useMemo(() => {
    if (!firstClear.scuttle) return undefined;
    const events = Object.entries(respawns)
      .filter(([k]) => k !== 'scuttle')
      .map(([id, time]) => ({ id: id as CampId, time: time! }))
      .filter((x) => x.time > firstClear.scuttle!)
      .sort((a, b) => a.time - b.time);
    return events[0];
  }, [firstClear, respawns]);

  // Base timing math
  const arrivalIfBase = useMemo(() => {
    if (!firstClear.scuttle) return undefined;
    return firstClear.scuttle + baseSeconds + walkSeconds;
  }, [firstClear, baseSeconds, walkSeconds]);

  const freeTimeIfBase = useMemo(() => {
    if (!nextAfterScuttle || arrivalIfBase == null) return undefined;
    return nextAfterScuttle.time - arrivalIfBase;
  }, [nextAfterScuttle, arrivalIfBase]);

  // XP & gold per-camp helpers
  const campXP = (id: CampId, monsterLevel: number, isFirstScuttle = false) => {
    if (id === 'scuttle') {
      const base = lerpByLevel(CAMPS.scuttle.xpRange!, monsterLevel, monsterLevelMax);
      return isFirstScuttle ? base * (CAMPS.scuttle.firstSpawnXPModifier ?? 1) : base;
    }
    if (id === 'wolves' && useWolvesOverride) return wolvesXPOverride(monsterLevel);
    return lerpByLevel(CAMPS[id].xpRange!, monsterLevel, monsterLevelMax);
  };

  const campGold = (id: CampId, monsterLevel: number) => {
    if (id === 'scuttle') return lerpByLevel(CAMPS.scuttle.goldRange!, monsterLevel, monsterLevelMax);
    return CAMPS[id].gold!;
  };

  // Simulate first clear sequence
  const xpGold = useMemo(() => {
    let totalXP = 0;
    let totalGold = 0;
    const events: Array<{
      id: CampId;
      time: number;
      monsterLvl: number;
      xp: number;
      gold: number;
      champLvl: number;
    }> = [];

    const campLevels: Record<CampId, number> = {
      blue: 1, gromp: 1, wolves: 1, raptors: 1, red: 1, krugs: 1, scuttle: 1,
    };

    for (const id of FIRST_CLEAR_ORDER) {
      const at = firstClear[id];
      if (!at) continue;
      const { level } = champLevelFromXP(totalXP);
      const monsterLvl = campLevels[id];
      const isFirstScut = id === 'scuttle' && at === CAMPS.scuttle.initial;
      const xpgain = campXP(id, monsterLvl, isFirstScut);
      const gold = campGold(id, monsterLvl);
      totalXP += xpgain;
      totalGold += gold;
      events.push({
        id, time: at, monsterLvl,
        xp: Math.round(xpgain),
        gold: Math.round(gold),
        champLvl: level,
      });
      campLevels[id] = Math.min(monsterLevelMax, monsterLvl + 1);
    }

    return { events, totals: { xp: Math.round(totalXP), gold: Math.round(totalGold) } };
  }, [firstClear, monsterLevelMax, useWolvesOverride]);

  // Waves with gold and XP values
  const waves = useMemo(() => {
    const out: Array<{ 
      n: number; 
      spawn: number; 
      arrive: number; 
      clear: number;
      gold: number;
      xp: number;
      hasCannonMinion: boolean;
    }> = [];
    
    for (let i = 1; i <= 30; i++) {
      const timing = getWaveTiming(i);
      const gold = getWaveGold(i);
      const xp = getWaveXP(i);
      
      out.push({ 
        n: i, 
        spawn: timing.spawn, 
        arrive: timing.arrival, 
        clear: timing.expectedClear,
        gold: gold.total,
        xp: xp.total,
        hasCannonMinion: i >= 3 && i % 3 === 0
      });
    }
    return out;
  }, []);

  return (
    <div className="jungle-simulator-page">
      <div className="jungle-simulator-container">
        <h1 className="jungle-simulator-title">
          <span className="title-gradient">Jungle Simulator</span>
        </h1>

       {/*  <MapCoordinatePlacer
        onChange={(pts) => {
          // Optionally capture the live points and store them
          // console.log('points', pts);
        }}
      />
 */}

          {/* NEW: interactive crude rift map */}
  <div className="card" style={{ marginBottom: 12 }}>
    <SummonersRiftMap clearSeconds={15} startAt={toSec('1:30')} />
  </div>


        <div className="jungle-simulator-content">
          <p className="jungle-simulator-description">
            Practice and optimize your first clear, see respawns, XP/gold, and align everything to wave timings.
          </p>

          {/* Configuration */}
          <div className="jungle-simulator-inputs">
            <h2 className="section-title">Configuration</h2>
            <div className="controls-grid card">
              <TimeInput label="Start time" value={start} onChange={setStart} />
              <TimeInput label="Scuttle time" value={scuttleTime} onChange={setScuttleTime} />
              <Toggle label="Take first Scuttle" value={takeScuttle} onChange={setTakeScuttle} />
              <NumberInput label="Base (recall) seconds" value={baseSeconds} onChange={setBaseSeconds} />
              <NumberInput label="Walk seconds (base → camp)" value={walkSeconds} onChange={setWalkSeconds} />
              <NumberInput label="Max monster level" value={monsterLevelMax} onChange={setMonsterLevelMax} />
              <TimeInput label="Other Scuttle killed at" value={otherScuttleKilledAt} onChange={setOtherScuttleKilledAt} />
              <Toggle label="Use Wolves per-level override" value={useWolvesOverride} onChange={setUseWolvesOverride} />
            </div>

            <div className="card">
              <h3 className="subsection-title">First Clear Segments (seconds after start / swap)</h3>
              <div className="segments-grid">
                <MiniNumber label="Blue @ +s" value={segment.blue} onChange={(v) => setSegment((d) => ({ ...d, blue: v }))} />
                <MiniNumber label="Gromp @ +s" value={segment.gromp} onChange={(v) => setSegment((d) => ({ ...d, gromp: v }))} />
                <MiniNumber label="Wolves @ +s" value={segment.wolves} onChange={(v) => setSegment((d) => ({ ...d, wolves: v }))} />
                <MiniNumber label="Raptors @ +s" value={segment.raptors} onChange={(v) => setSegment((d) => ({ ...d, raptors: v }))} />
                <MiniNumber label="Red @ +s" value={segment.red} onChange={(v) => setSegment((d) => ({ ...d, red: v }))} />
                <MiniNumber label="Krugs @ +s" value={segment.krugs} onChange={(v) => setSegment((d) => ({ ...d, krugs: v }))} />
              </div>
              <p className="hint">Quadrant A: Blue → Gromp → Wolves. Quadrant B: Raptors → Red → Krugs.</p>
            </div>
          </div>

          {/* Results */}
          <div className="jungle-simulator-results">
            <h2 className="section-title">Results</h2>

            <div className="quick-answers">
              <Quick label="Next camp up after Scuttle">
                {nextAfterScuttle ? (
                  <>
                    <CampTag id={nextAfterScuttle.id} /> at <b>{fmt(nextAfterScuttle.time)}</b>
                  </>
                ) : '—'}
              </Quick>
              <Quick label="If you base + walk, you arrive">{arrivalIfBase != null ? fmt(arrivalIfBase) : '—'}</Quick>
              <Quick label="Free time before that spawn">
                {freeTimeIfBase != null ? (freeTimeIfBase >= 0 ? `${fmt(freeTimeIfBase)} early` : `${fmt(-freeTimeIfBase)} late`) : '—'}
              </Quick>
              <Quick label="Total after first clear (incl. Scuttle)">
                {`${xpGold.totals.xp} XP, ${xpGold.totals.gold}g`}
              </Quick>
              <Quick label="Approx champion level after first clear">
                {champLevelFromXP(xpGold.totals.xp).level}
              </Quick>
              <Quick label="Next Scuttle spawn">{respawns.scuttle ? fmt(respawns.scuttle) : '—'}</Quick>
            </div>

            <div className="tables">
              <div className="card table-card">
                <h3 className="subsection-title">First-Clear Events</h3>
                <table className="js-table">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Event</th>
                      <th>Group</th>
                      <th>Camp Lvl</th>
                      <th>XP</th>
                      <th>Gold</th>
                      <th>Champ Lvl (pre)</th>
                      <th>Next respawn</th>
                    </tr>
                  </thead>
                  <tbody>
                    {xpGold.events.map((e) => {
                      const group = CAMPS[e.id].group;
                      return (
                        <tr key={`${e.id}-${e.time}`} className={`camp-row group-${group}`}>
                          <td>{fmt(e.time)}</td>
                          <td className="event-cell">
                            <span className="event-name">{CAMPS[e.id].name}</span>
                          </td>
                          <td><CampTag id={e.id} /></td>
                          <td>{e.monsterLvl}</td>
                          <td className="xp">{e.xp}</td>
                          <td className="gold">{e.gold}</td>
                          <td>{e.champLvl}</td>
                          <td>
                            {e.id !== 'scuttle'
                              ? fmt(e.time + CAMPS[e.id].respawn)
                              : respawns.scuttle
                                ? fmt(respawns.scuttle)
                                : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="card">
                <h3 className="subsection-title">Gold & XP Comparisons</h3>
                <div className="comparison-grid">
                  <div className="comparison-section">
                    <h4 className="comparison-title">Waves (per wave)</h4>
                    <ul className="comparison-list">
                      <li>
                        <span className="comparison-label">Regular wave (3 melee + 3 caster):</span>
                        <span className="comparison-value">{getWaveGold(1).total}g / {getWaveXP(1).total} XP</span>
                      </li>
                      <li className="cannon-highlight">
                        <span className="comparison-label">Cannon wave (+ 1 cannon):</span>
                        <span className="comparison-value">{getWaveGold(3).total}g / {getWaveXP(3).total} XP</span>
                      </li>
                    </ul>
                  </div>
                  <div className="comparison-section">
                    <h4 className="comparison-title">Jungle Camps (per camp)</h4>
                    <ul className="comparison-list">
                      <li><span className="comparison-label">Blue Sentinel:</span><span className="comparison-value">90g / 95-143 XP</span></li>
                      <li><span className="comparison-label">Red Brambleback:</span><span className="comparison-value">90g / 95-143 XP</span></li>
                      <li><span className="comparison-label">Gromp:</span><span className="comparison-value">80g / 120-180 XP</span></li>
                      <li><span className="comparison-label">Wolves:</span><span className="comparison-value">81g / 80-120 XP</span></li>
                      <li><span className="comparison-label">Raptors:</span><span className="comparison-value">75g / 70-105 XP</span></li>
                      <li><span className="comparison-label">Krugs:</span><span className="comparison-value">81g / 89-134 XP</span></li>
                      <li><span className="comparison-label">Scuttle:</span><span className="comparison-value">55-121g / 100-150 XP</span></li>
                    </ul>
                  </div>
                </div>
                <div className="comparison-note">
                  💡 <b>Missing a cannon wave (165g) ≈ missing Red + Gromp (170g)</b>
                </div>
              </div>

              <div className="card table-card">
                <h3 className="subsection-title">Waves vs. Jungle Events</h3>
                <table className="js-table">
                  <thead>
                    <tr>
                      <th>Wave #</th>
                      <th>Spawn</th>
                      <th>Arrive</th>
                      <th>Clear</th>
                      <th>Gold</th>
                      <th>XP</th>
                      <th>Your events in this 30s window</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waves.slice(1, 21).map((w) => {
                      const list = xpGold.events.filter((e) => e.time >= w.spawn && e.time < w.spawn + 30);
                      return (
                        <tr key={w.n} className={w.hasCannonMinion ? 'cannon-wave' : ''}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {w.n}
                              {w.hasCannonMinion && <span className="cannon-indicator" title="Cannon wave">🎯</span>}
                            </div>
                          </td>
                          <td>{fmt(w.spawn)}</td>
                          <td>{fmt(w.arrive)}</td>
                          <td>{fmt(w.clear)}</td>
                          <td className="gold">{w.gold}g</td>
                          <td className="xp">{w.xp}</td>
                          <td>
                            {list.length ? (
                              <ul className="event-list">
                                {list.map((e) => (
                                  <li key={`${e.id}-${e.time}`} className={`dot dot-${CAMPS[e.id].group}`}>
                                    <span className="event-dot" />
                                    {`${fmt(e.time)} – ${CAMPS[e.id].name}`}
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="card">
                <h3 className="subsection-title">Objective Windows (S14+)</h3>
                <ul className="bullet">
                  <li>Dragon spawns at <b>5:00</b>; respawns 5:00 after each kill.</li>
                  <li>
                    Voidgrubs spawn at <b>5:00</b>. If all 3 die before <b>9:45</b>, a second set can respawn <b>+4:00</b>.
                    All grubs despawn at <b>13:45</b> (13:55 if in combat). Rift Herald spawns at <b>14:00</b>.
                  </li>
                  <li>
                    Rift Scuttler first pair spawns at <b>3:30</b> with <b>−80% XP</b>; afterward one scuttle is up at a
                    time, respawning <b>2:30</b> after the last of the first pair dies.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div> 
      </div>
    </div>
  );
};

/* ---------- Local UI bits ---------- */
function TimeInput({
  label, value, onChange,
}: { label: string; value: number; onChange: (v: number) => void }) {
  const [txt, setTxt] = useState<string>(fmt(value));
  return (
    <label className="control">
      <span>{label}</span>
      <input
        className="input"
        value={txt}
        onChange={(e) => setTxt(e.target.value)}
        onBlur={() => onChange(toSec(txt))}
        placeholder="m:ss"
      />
    </label>
  );
}

function NumberInput({
  label, value, onChange, step = 1,
}: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label className="control">
      <span>{label}</span>
      <input
        type="number"
        className="input"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function MiniNumber({
  label, value, onChange, step = 1,
}: { label: string; value: number; onChange: (v: number) => void; step?: number }) {
  return (
    <label className="mini-number">
      <span>{label}</span>
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="input"
      />
    </label>
  );
}

function Toggle({
  label, value, onChange,
}: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function Quick({ label, children }: React.PropsWithChildren<{ label: string }>) {
  return (
    <div className="quick">
      <span className="muted">{label}</span>
      <span>{children}</span>
    </div>
  );
}

function CampTag({ id }: { id: CampId }) {
  const group = CAMPS[id].group; // 'blue' | 'red' | 'river'
  return (
    <span className={`camp-chip camp-${group}`} title={group}>
      {group === 'blue' ? 'Blue Side' : group === 'red' ? 'Red Side' : 'River'}
    </span>
  );
}

export default JungleSimulatorPage;
